import { Modal, Select, Input, message, Button } from "antd";
import { CloudSyncOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import axios from "axios";

const UpdateTask = ({ visible, onClose, id, taskToEdit }) => {
  const [stories, setStories] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [members, setMembers] = useState([]);

  const [existingTasks, setExistingTasks] = useState([]);

  const [selectedStory, setSelectedStory] = useState(null);
  const [taskDescription, setTaskDescription] = useState("");
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [taskType, setTaskType] = useState(null);
  const [assignedMember, setAssignedMember] = useState(null);

  useEffect(() => {
    if (visible && taskToEdit) {
      setSelectedStory(taskToEdit.story || null);
      setTaskDescription(taskToEdit.description || "");
      setSelectedCharacter(taskToEdit.characterId || null);
      setTaskType(taskToEdit.task || null);
      setAssignedMember(taskToEdit.member || null);
      fetchStories();
      handleStoryChange(taskToEdit.story);
    }
  }, [visible, taskToEdit]);

  const fetchStories = async () => {
    try {
      const res = await axios.get("/api/stories");
      setStories(res.data);
    } catch (error) {
      message.error("Fehler beim Laden der Geschichten.");
    }
  };

  const fetchExistingTasks = async (storyId) => {
    try {
      const tasksResponse = await axios.get(`/api/tasks/${id}/${storyId}`);
      setExistingTasks(tasksResponse.data); // Store tasks to filter characters
    } catch (error) {
      message.error("Fehler beim Laden der Aufgaben.");
    }
  };

  const handleStoryChange = async (storyId) => {
    if (!storyId) return;
    setSelectedStory(storyId);
    setCharacters([]);
    setMembers([]);

    try {
      const charResponse = await axios.get(
        `/api/characters/?storycharid=${storyId}`
      );
      setCharacters(charResponse.data);
      const membersResponse = await axios.get(`/api/groups/${id}/members`);
      setMembers(membersResponse.data);

      // Fetch existing tasks for this story
      await fetchExistingTasks(storyId);
    } catch (error) {
      message.error("Fehler beim Laden der Daten.");
    }
  };

  // Filter characters to exclude those that already have a task of the selected type
  const getAvailableCharacters = () => {
    if (!taskType) return characters;

    // Convert character_ids from CSV string to an array
    const charactersWithTask = new Set(
      existingTasks
        .filter((task) => task.category === taskType)
        .flatMap((task) =>
          task.character_ids ? task.character_ids.split(",").map(Number) : []
        )
    );

    console.log(
      "Filtered Available Characters:",
      characters.filter((char) => !charactersWithTask.has(char.id))
    );

    return characters.filter(
      (char) => !charactersWithTask.has(Number(char.id))
    );
  };

  const handleSubmit = async () => {
    if (!taskToEdit || !taskToEdit.taskId) {
      message.error("Keine gültige Aufgabe zum Aktualisieren.");
      return;
    }
    if (
      !selectedStory ||
      !taskDescription ||
      !selectedCharacter ||
      !taskType ||
      !assignedMember
    ) {
      message.error("Bitte fülle alle Felder aus.");
      return;
    }

    try {
      await axios.put(`/api/tasks/${taskToEdit.taskId}`, {
        storyId: selectedStory,
        characterId: selectedCharacter, // Single ID instead of array
        description: taskDescription,
        category: taskType,
        memberId: assignedMember,
      });

      message.success("Aufgabe erfolgreich aktualisiert!");
      handleClose();
    } catch (error) {
      message.error("Fehler beim Aktualisieren der Aufgabe.");
    }
  };

  const handleClose = () => {
    setSelectedStory(null);
    setTaskDescription("");
    setSelectedCharacter(null);
    setTaskType(null);
    setAssignedMember(null);
    onClose();
  };

  return (
    <Modal
      title="Aufgabe bearbeiten"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Abbrechen
        </Button>,
        <Button key="update" type="primary" onClick={handleSubmit}>
          Aktualisieren
        </Button>,
      ]}
    >
      <Select
        placeholder="Wähle eine Geschichte"
        style={{ width: "100%", marginBottom: "10px" }}
        value={selectedStory}
        onChange={handleStoryChange}
      >
        {stories.map((story) => (
          <Select.Option key={story.id} value={story.id}>
            {story.title}
          </Select.Option>
        ))}
      </Select>

      <Input
        placeholder="Aufgabenbeschreibung"
        value={taskDescription}
        onChange={(e) => setTaskDescription(e.target.value)}
        style={{ marginBottom: "10px" }}
      />

      <Select
        placeholder="Aufgabentyp wählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={taskType}
        onChange={(value) => setTaskType(value)}
      >
        <Select.Option value="record">
          🎙 <span style={{ fontSize: "1.5em" }}>Aufnehmen</span>
        </Select.Option>
        <Select.Option value="review">
          ✏ <span style={{ fontSize: "1.5em" }}>Feedback geben</span>
        </Select.Option>
        <Select.Option value="analyze">
          🌥 <span style={{ fontSize: "1.5em" }}>Wortwolke</span>
        </Select.Option>
        <Select.Option value="enrichments">
          🎨 <span style={{ fontSize: "1.5em" }}>Gestalten</span>
        </Select.Option>
      </Select>

      {/* Character Selection (Loaded based on selected story) */}
      <Select
        disabled={!taskType} // Disable until task type is selected
        mode="multiple"
        placeholder="Charaktere auswählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={selectedCharacters} // Bind to selectedCharacters state
        onChange={(value) => setSelectedCharacters(value)} // Update state on change
      >
        {getAvailableCharacters().map((char) => (
          <Select.Option key={char.id} value={char.id}>
            {char.name}
          </Select.Option>
        ))}
      </Select>

      <Select
        placeholder="Mitglied auswählen"
        style={{ width: "100%", marginBottom: "10px" }}
        value={assignedMember}
        onChange={(value) => setAssignedMember(value)}
      >
        {members.map((member) => (
          <Select.Option key={member.id} value={member.id}>
            {member.username}
          </Select.Option>
        ))}
      </Select>

      <Button
        icon={<CloudSyncOutlined />}
        type="primary"
        onClick={handleSubmit}
      >
        Aktualisieren
      </Button>
    </Modal>
  );
};

export default UpdateTask;
